import { useCallback } from 'react';
import { useTranslation } from '../context/TranslationContext';
import useShippingConfig, {
  formatCitiesForDisplay,
  // formatCountriesForDisplay,
} from './useShippingConfig';

/**
 * Pure validation helper — exported for tests, SSR, and any non-React callers.
 * Validates an address form against the active shipping config, returning a
 * localized error message that always references the live deliverable list
 * when one is available (otherwise a generic "unavailable in your area"
 * fallback).
 *
 * The `scope` parameter decides whether delivery-zone checks apply:
 *   - 'shipping' (default): country AND city must both be in the deliverable
 *     lists, because the goods ship to this address.
 *   - 'billing': NO checks apply. The address is purely for invoicing, so
 *     tourists/foreigners can save a non-Swiss country and any city. The
 *     delivery-zone copy ("We currently deliver to: …") is misleading on a
 *     billing form.
 *
 * @param {object} form                    Address form object (`country`, `city` required).
 * @param {string[]} validCountries        Lowercase ISO/short country names that the store delivers to.
 * @param {string[]} validCities           Lowercase city names that the store delivers to.
 * @param {(key: string, fallback: string) => string} t  Translator function.
 * @param {'shipping' | 'billing'} [scope='shipping']  Which address kind is being validated.
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateAddressPure(form, validCountries, validCities, t, scope = 'shipping') {
  // const countriesList = formatCountriesForDisplay(validCountries);
  const citiesList = formatCitiesForDisplay(validCities);
  const inputCountry = (form?.country || '').toLowerCase().trim();
  const inputCity = (form?.city || '').toLowerCase().trim();

  // Skip the country check for billing addresses — billing is purely for
  // invoicing, so tourists/foreigners can save a non-Swiss billing country.
  if (scope === 'shipping' && !validCountries.includes(inputCountry)) {
    return {
      ok: false,
      message: citiesList
        ? t(
            'invalidCountryWithList',
            'Delivery to this country is not available. We only ship to {citiesList} in Switzerland.'
          ).replace('{citiesList}', citiesList)
        : t('invalidCountryEmpty', 'Delivery is currently unavailable in your area.'),
    };
  }

  // Skip the city check for billing addresses — they are not a delivery
  // destination, so the "we currently deliver to: …" copy is misleading.
  if (scope === 'shipping' && !validCities.includes(inputCity)) {
    return {
      ok: false,
      message: citiesList
        ? t(
            'invalidCityWithList',
            'Delivery to this city is not available. We currently deliver to: {citiesList}.'
          ).replace('{citiesList}', citiesList)
        : t('invalidCityEmpty', 'Delivery is currently unavailable in your area.'),
    };
  }

  return { ok: true };
}

/**
 * React hook wrapping `validateAddressPure` with the live translation context
 * and the loaded shipping config. Returns a stable `validateAddress(form, scope)`
 * function suitable for use in submit handlers.
 *
 * @returns {{ validateAddress: (form: object, scope?: 'shipping' | 'billing') => { ok: true } | { ok: false, message: string } }}
 */
export default function useAddressValidation() {
  const { t } = useTranslation();
  const { cities: validCities, countries: validCountries } = useShippingConfig();

  const validateAddress = useCallback(
    (form, scope = 'shipping') =>
      validateAddressPure(form, validCountries, validCities, t, scope),
    [t, validCities, validCountries]
  );

  return { validateAddress };
}
